import {
  classNames,
  determineScoreError,
  isStandard,
  isStatusPreGame
} from '@library/utils';
import { isStatusInProgress, isStatusTradeable } from '@library/utils/status';
import { useEffect, useRef } from 'react';
import { AutoTradeIndicator } from '@library/components/AutoTradeIndicator';
import { Button } from '@library/components/buttons';
import { GameModesSelect } from '@library/components/selects/GameModesSelect';
import type { GamePlayerProjectionTrading } from '@library/types/trading';
import { InGameIndicator } from '@library/components/InGameIndicator';
import { OddsTypeIndicator } from '@library/components/OddsTypeIndicator';
import { ProjectionInput } from '@library/components/inputs/ProjectionInput';
import { ProjectionKebabMenu } from '@library/components/menus/ProjectionKebabMenu';
import { PromoIndicator } from '@library/components/PromoIndicator';
import type { SelectOptions } from '@library/types/utils';
import { SimIndicator } from '@library/components/SimIndicator';
import type { Status } from '@library/types/projection';
import { StatusSelect } from '@library/components/selects';
import { UsersIcon } from '@heroicons/react/24/outline';
import { formatProjectionValueNumberString } from '@bo-utils/projectionDisplay';
import { onAutoTradeInGameChange } from '@bo-utils/projection';
import { useGameModes } from '@bo-hooks/gameModes/useGameModes';
import { useGetAverageScoreMutation } from '@bo-services/projectionsApi';
import { useSplitFlag } from '@bo-hooks/useSplitFlag';

export interface TradingProjectionProps {
  change?: Partial<GamePlayerProjectionTrading>;
  playerId: string;
  projection: GamePlayerProjectionTrading;
  updateProjection: ({
    change,
    initialProjection
  }: {
    change: Partial<GamePlayerProjectionTrading>;
    initialProjection: GamePlayerProjectionTrading;
  }) => void;
}

export const TradingProjection = ({
  change = {},
  playerId,
  projection,
  updateProjection
}: TradingProjectionProps) => {
  const displayProjection = {
    ...projection,
    ...change
  };

  const {
    autoTrade,
    averageScore,
    boardTime,
    discountPercentage,
    dataSource,
    durationId,
    foreignId,
    gameId,
    gameModeIds,
    hasSuggestedLines,
    inGame,
    isCommunityPlay,
    isPromo,
    oddsType,
    optIn = false,
    probabilityOfMore,
    projectionId,
    projectionValue,
    statType,
    statTypeId,
    status
  } = displayProjection;

  const { projectionValue: initialValue } = projection ?? {
    projectionValue: ''
  };

  const { gameModeSelectOptions: gameModeOptions } = useGameModes({});
  const gameModeValues = gameModeIds?.map((id) => String(id)) ?? [];

  const valueChanged = initialValue !== projectionValue;

  const avgScoreError = determineScoreError({
    averageScore,
    projectionValue
  });

  const [getAverageScore, { isSuccess, data, isUninitialized }] =
    useGetAverageScoreMutation();

  useEffect(() => {
    if (
      change.projectionValue === projection.projectionValue &&
      change.autoTrade === projection.autoTrade &&
      change.inGame === projection.inGame &&
      change.gameModeIds === projection.gameModeIds
    ) {
      updateProjection({
        change: { autoTrade, inGame, gameModeIds, projectionValue },
        initialProjection: projection
      });
    }
  }, [
    autoTrade,
    change,
    inGame,
    gameModeIds,
    projection,
    projectionValue,
    updateProjection
  ]);

  useEffect(() => {
    if (!averageScore && isSuccess && data?.[0]?.score_avg) {
      const newAverageScore = Number(data[0].score_avg);

      updateProjection({
        change: { averageScore: newAverageScore },
        initialProjection: projection
      });
    }
  }, [
    averageScore,
    data,
    gameId,
    isSuccess,
    projection,
    projectionId,
    projectionValue,
    updateProjection
  ]);

  const handleValueChange = (newValue: string) => {
    const newValueNumber = newValue === '' ? newValue : Number(newValue);

    if (
      (isUninitialized || (!averageScore && data?.[0]?.score_avg)) &&
      newValueNumber
    ) {
      getAverageScore([
        {
          duration_id: Number(durationId),
          foreign_id: foreignId,
          player_id: Number(playerId),
          stat_type_id: Number(statTypeId)
        }
      ]).catch(() => {});
    }

    updateProjection({
      change: { autoTrade, projectionValue: newValueNumber },
      initialProjection: projection
    });
  };

  const handleStatusChange = (newStatus: Status) => {
    updateProjection({
      change: { status: newStatus },
      initialProjection: projection
    });
  };

  const handleGameModeOptionsChange = (newGameModes: SelectOptions) => {
    const selectedGameModes = newGameModes.map(({ key }) => Number(key));

    updateProjection({
      change: { gameModeIds: selectedGameModes },
      initialProjection: projection
    });
  };

  const handleAutoTradeChange = () => {
    updateProjection({
      change: {
        ...onAutoTradeInGameChange(autoTrade, inGame),
        projectionValue
      },
      initialProjection: projection
    });
  };

  const handleInGameChange = () => {
    updateProjection({
      change: {
        ...onAutoTradeInGameChange(autoTrade, inGame, false),
        projectionValue
      },
      initialProjection: projection
    });
  };

  const { isActive: isAFXActive } = useSplitFlag({
    flagName: 'AUTO_TRADER_ENABLED'
  });

  const isPregame = isStatusPreGame(status);
  const isInGameInProgress = isStatusInProgress(status) && inGame;
  const isFullOpacity = isPregame || isInGameInProgress;
  const showOdds = !isStandard(oddsType);
  const isTradeable = isStatusTradeable(status);
  const showSim = dataSource === 'simulation';
  const disableInput = !isTradeable && !isInGameInProgress;
  const hideInput = disableInput || (!avgScoreError && !valueChanged);
  const ref = useRef<HTMLInputElement>(null);

  return (
    <div className="group/projection relative flex h-14 leading-5">
      <div className="hover:bg-soFresh-120 focus-within:bg-soFresh-120 relative min-w-[11.3rem] rounded-md">
        <div className="grid h-full grid-flow-row grid-rows-2 gap-2 pl-2 pr-3">
          <div className="flex h-4 flex-row self-end font-medium">
            <div className="group/proj-input flex flex-row">
              <ProjectionInput
                className={classNames(
                  '!h-5 !w-0 overflow-hidden rounded-md border-none !bg-transparent !text-base focus:!w-10 focus:border-solid',
                  !isFullOpacity ? 'opacity-50' : '',
                  avgScoreError
                    ? '!border-roses focus:border-roses !w-10 !border-solid'
                    : '',
                  valueChanged && !avgScoreError
                    ? '!border-msJackson !w-10 !border-solid'
                    : ''
                )}
                disabled={disableInput}
                id={`${projectionId}-projection-display`}
                data-testid="projection-input"
                onValueChange={handleValueChange}
                ref={ref}
                value={projectionValue}
              />
              <Button
                className={classNames(
                  'group-focus-within/proj-input:hidden',
                  !isFullOpacity ? 'opacity-50' : '',
                  disableInput ? 'cursor-not-allowed' : 'cursor-text',
                  !hideInput ? 'hidden' : ''
                )}
                onClick={() => {
                  ref.current?.focus();
                }}
                disabled={disableInput}
                id={`${projectionId}-projection-display-button`}
                data-testid="projection-input-button"
              >
                {formatProjectionValueNumberString(String(projectionValue))}
              </Button>
              {isAFXActive && (
                <>
                  <AutoTradeIndicator
                    autoTrade={autoTrade}
                    className="h-5 pl-1"
                    hasSuggestedLines={hasSuggestedLines}
                    isTrading
                  />
                  {inGame && (
                    <InGameIndicator
                      autoTrade={autoTrade}
                      className="h-5 py-px pl-1"
                      inGame={inGame}
                    />
                  )}
                </>
              )}
            </div>

            {discountPercentage && (
              <span
                className={classNames(
                  'text-atlien-100 cursor-not-allowed px-2',
                  !isFullOpacity ? 'opacity-50' : ''
                )}
              >
                {`(${discountPercentage}%)`}
              </span>
            )}

            {isCommunityPlay && (
              <UsersIcon
                title="Community Play"
                className="text-msJackson ml-1 size-4"
              />
            )}

            <div className="ml-auto flex flex-row items-center gap-2 px-2">
              {isPromo && <PromoIndicator className="size-4" optIn={optIn} />}

              {showOdds && (
                <OddsTypeIndicator className="size-4" oddsType={oddsType} />
              )}

              <SimIndicator isSimulation={showSim} className="size-4" />

              <StatusSelect
                className=""
                containerClassName="h-5"
                boardTime={boardTime}
                iconOnly
                label={`tradingDash${projectionId}Status`}
                onChange={handleStatusChange}
                value={status}
              />
              <GameModesSelect
                height="h-5"
                gameModeOptions={gameModeOptions}
                gameModeValues={gameModeValues}
                onChange={handleGameModeOptionsChange}
                iconOnly
              />
            </div>
          </div>
          <div className="text-soClean-160 flex h-4 w-full min-w-24 text-xs">
            <span className={classNames(!isFullOpacity ? 'opacity-50' : '')}>
              {statType}
            </span>
            <div className="grow" />
            {probabilityOfMore && (
              <span
                title={String(probabilityOfMore)}
              >{`${Number(probabilityOfMore * 100).toFixed(0)}%`}</span>
            )}
          </div>
        </div>

        {isAFXActive && (
          <ProjectionKebabMenu
            autoTrade={autoTrade}
            inGame={inGame}
            onAutoTradeChange={handleAutoTradeChange}
            onInGameChange={handleInGameChange}
          />
        )}
      </div>
      <div className="border-soFresh-130 h-8 self-center border-r border-solid" />
    </div>
  );
};
